<template>
  <div :data-tenant="config.tenant">
    <header id="region_header">
      <router-link class="logo" to="/">
        <img :alt="`${config.branding.shortName} logo`" :src="logoUrl" />
        <span v-if="config.branding.headerText" class="branding-text">
          {{ config.branding.headerText }}
        </span>
      </router-link>

      <nav>
        <router-link to="/">Home</router-link>
        <router-link to="/transfer">{{ config.content.nav.transferLabel }}</router-link>

        <template v-if="savedPlansEnabled">
          <a v-if="!loginSuccess" data-toggle="modal" data-target="#LoginModal">Login</a>
          <router-link v-if="loginSuccess" to="/GeneratedPlans">Generated Plans</router-link>
          <router-link v-if="loginSuccess" to="/" @click="handleLogout">Logout</router-link>
        </template>
      </nav>
    </header>

    <div class="main-content">
      <router-view />

      <LoginModal
        v-if="savedPlansEnabled"
        modalID="LoginModal"
        :setLoginSucess="setLoginSuccess"
        ref="loginModal"
      />
    </div>

    <footer id="footer_site">
      <div class="container-fluid">
        <div class="row">
          <div class="col-md-6">
            <div class="text-center-sm text-center-xs text-left" id="footer_logo">
              <img :src="footerLogoUrl" :alt="`${config.branding.shortName} logo`" />
            </div>
          </div>

          <div class="col-md-6">
            <div class="text-center-sm text-center-xs text-right" id="footer_menu">
              <ul>
                <li><router-link to="/">Home</router-link></li>
                <li><a href="/">About us</a></li>
              </ul>
            </div>
          </div>
        </div>

        <div class="row">
          <hr />
          <div class="col-md-12">
            <div class="text-center" id="footer_copyright">
              {{ config.content.footer.addressLine }}<br />
              <span v-if="config.content.footer.contactLine">
                {{ config.content.footer.contactLine }}<br />
              </span>
              {{ config.content.footer.copyrightLine }}
            </div>
          </div>
        </div>
      </div>
    </footer>
  </div>
</template>


<script lang="ts">
import { defineComponent, computed } from "vue";
import LoginModal from "@/components/LoginModal.vue";
import store from "@/store";
import { useTenantConfig } from "@/composables/useTenantConfig";
import { useFeatureFlags } from "@/composables/useFeatureFlags";

export default defineComponent({
  name: "App",
  components: {
    LoginModal,
  },
  setup() {
    const { config } = useTenantConfig();
    const { savedPlansEnabled } = useFeatureFlags();

    // Compute logo URL from config
    const logoUrl = computed(() => {
      return new URL(`./assets/${config.branding.logo}`, import.meta.url).href;
    });

    // Compute footer logo URL (falls back to main logo if not specified)
    const footerLogoUrl = computed(() => {
      const path = config.branding.footerLogo || config.branding.logo;
      return new URL(`./assets/${path}`, import.meta.url).href;
    });

    // Set page title based on tenant
    document.title = `${config.branding.shortName} Transfer Portal`;

    return {
      config,
      savedPlansEnabled,
      logoUrl,
      footerLogoUrl,
    };
  },
  data() {
    return {
      loginSuccess: localStorage.getItem("userToken") !== null,
    };
  },
  methods: {
    setLoginSuccess() {
      this.loginSuccess = true;
    },
    handleLogout() {
      this.loginSuccess = false;
      store.methods.logout();
    },
  },
  created() {
    store.methods.initUserSession();
  },
});
</script>

<style lang="scss">
@import "@/styles/_variables.scss";
@import "@/styles/tenants/arizona.scss";

html,
body {
  height: 100%;
}

[data-tenant] {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.main-content {
  flex: 1;
}

a {
  cursor: pointer;
}

// Header and footer base styles are in layout.scss
// These are additional styles specific to App.vue elements

#footer_site {
  padding: 20px 0;
  margin-top: auto;

  #footer_logo img {
    height: $logo-height;
    width: auto;
  }

  #footer_menu ul {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    gap: 20px;
    justify-content: flex-end;

    li {
      display: inline;
    }
  }

  #footer_copyright {
    font-size: 0.85rem;
    opacity: 0.9;
  }

  hr {
    border-color: rgba(0, 0, 0, 0.1);
  }
}
</style>
